/**
 * Deleting an invoice or a variation invoice needs an Admin's approval (BUG-006, owner decision 2026-10-04).
 *
 * Anyone who may edit Invoicing can ASK for an invoice to be deleted ("Request deletion…", optional reason); the row
 * stays real (totals unchanged) and shows "Deletion requested · <name>" until an Admin / Super Admin approves it
 * ("Approve deletion…" = the old delete, with Undo + History) or rejects it ("Reject deletion…", a reason the
 * requester is told). The requester can cancel their request; admins deleting on their own still delete at once.
 * The server enforces all of it (backend/invoice_deletion.py); the menus here only offer what the server allows.
 *
 * Used by the Invoicing register (pages/InvoicingPage.js), the project Invoicing tab (InvoicingPanels.InvoicesTable)
 * and the VO invoices on the Variations tab (VariationsPanels.VariationDetails):
 *
 *   const deletion = useInvoiceDeletion({ onChanged: reload, onDeleted: (invoice) => unselect(invoice.id) });
 *   <RowActions items={[...otherItems, ...deletion.menuItems(invoice, { canEdit })]} />
 *   <DeletionRequestPill invoice={invoice} />
 *   rows.map(deletion.apply).filter(Boolean)   // the change shows at once, before `onChanged` reloads
 */
import { useCallback, useRef, useState } from 'react';
import { CircleX, Trash2, Undo2 } from 'lucide-react';
import api from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { formatDate, formatMoney } from '@/lib/format';
import { getErrorMessage } from '@/lib/errors';
import { useConfirm } from '@/components/ui/confirm-dialog';
import { notify } from '@/components/ui/notify';
import { StatusPill } from '@/components/ui/status-pill';
import { Textarea } from '@/components/ui/textarea';
import {
  MAX_REASON, deletionPillText, deletionPillTitle, deletionRequestOf, invoiceApiPath, invoiceLabel, isApprover, isVariationInvoice,
} from './invoiceDeletionModel';

export {
  APPROVER_ROLES, MAX_REASON, deletionPillText, deletionPillTitle, deletionRequestOf, invoiceApiPath, invoiceLabel, isApprover,
  isVariationInvoice,
} from './invoiceDeletionModel';

const text = (value) => String(value ?? '').trim();
const capital = (value) => (value ? value.charAt(0).toUpperCase() + value.slice(1) : value);

/**
 * The ⋯ menu items for deleting one invoice, for this user (falsy entries are skipped by RowActions).
 *   no request · admin       Delete invoice…            (deleted at once; Undo in the toast)
 *   no request · editor      Request deletion…
 *   request    · admin       Approve deletion… · Reject deletion… (Cancel instead of Reject when it is their own)
 *   request    · requester   Cancel deletion request
 *   request    · others      "Deletion requested by …" (disabled, says who decides)
 * testIdPrefix keeps the existing `invoice-delete-<id>` test id of the project Invoicing tab.
 */
export function deletionMenuItems(invoice, { user, canEdit, actions, testIdPrefix = 'invoice' }) {
  if (!invoice?.id || !canEdit) return [];
  const id = invoice.id;
  const approver = isApprover(user);
  const request = deletionRequestOf(invoice);
  if (!request) {
    return [approver
      ? { label: 'Delete invoice…', icon: Trash2, danger: true, onSelect: () => actions.remove(invoice), testId: `${testIdPrefix}-delete-${id}` }
      : { label: 'Request deletion…', icon: Trash2, danger: true, onSelect: () => actions.request(invoice), testId: `${testIdPrefix}-request-deletion-${id}` }];
  }
  const mine = text(request.requested_by_id) && text(request.requested_by_id) === text(user?.id || user?.sub);
  if (approver) {
    return [
      mine
        ? { label: 'Cancel deletion request', icon: Undo2, onSelect: () => actions.cancel(invoice), testId: `${testIdPrefix}-cancel-deletion-${id}` }
        : { label: 'Reject deletion…', icon: CircleX, onSelect: () => actions.reject(invoice), testId: `${testIdPrefix}-reject-deletion-${id}` },
      { label: 'Approve deletion…', icon: Trash2, danger: true, onSelect: () => actions.approve(invoice), testId: `${testIdPrefix}-approve-deletion-${id}` },
    ];
  }
  if (mine) {
    return [{ label: 'Cancel deletion request', icon: Undo2, onSelect: () => actions.cancel(invoice), testId: `${testIdPrefix}-cancel-deletion-${id}` }];
  }
  return [{
    label: `Deletion requested by ${text(request.requested_by_name) || 'someone'}`,
    icon: Trash2,
    disabled: true,
    hint: 'Admin decides',
    testId: `${testIdPrefix}-deletion-pending-${id}`,
  }];
}

/** "Deletion requested · <name>" on a row; nothing when no request is open. */
export function DeletionRequestPill({ invoice, className, testId }) {
  const request = deletionRequestOf(invoice);
  if (!request) return null;
  return (
    <StatusPill
      tone="red"
      size="sm"
      icon={Trash2}
      label={deletionPillText(request)}
      title={deletionPillTitle(request)}
      className={className}
      data-testid={testId || `invoice-deletion-pill-${invoice.id}`}
    />
  );
}

function ReasonField({ inputRef, id, label, required = false, placeholder, testId }) {
  return (
    <div className="flex flex-col gap-1.5">
      <label htmlFor={id} className="text-[12.5px] font-semibold text-ink-2">
        {label}{required ? null : <span className="font-normal text-ink-3"> (optional)</span>}
      </label>
      <Textarea id={id} ref={inputRef} rows={3} maxLength={MAX_REASON} placeholder={placeholder} data-testid={testId} />
    </div>
  );
}

const subtitleOf = (invoice) => [
  text(invoice.invoice_type) || 'Invoice',
  formatMoney(invoice.amount),
  text(invoice.status) || 'Not Started',
].join(' · ');

const quickBooksNote = (invoice, tail) => (invoice?.quickbooks?.invoice_id
  ? `It stays in QuickBooks — delete it there too. ${tail}`
  : tail);

/**
 * Request / approve / reject / cancel / delete for one invoice at a time, with the app's confirm dialog.
 * onChanged: reload the page's invoices afterwards. Until it has (or when a screen has no reload), `apply`
 * shows the change on the rows the screen already has.
 * onDeleted(invoice): an invoice is really gone (an admin's delete or approval), e.g. to drop it from a selection.
 */
export function useInvoiceDeletion({ onChanged, onDeleted } = {}) {
  const { user } = useAuth() || {};
  const confirm = useConfirm();
  const reasonRef = useRef(null);
  const [patches, setPatches] = useState({});

  const settle = useCallback(async (invoice, patch) => {
    setPatches((prev) => ({ ...prev, [invoice.id]: patch }));
    if (patch.deleted) onDeleted?.(invoice);
    if (!onChanged) return;
    try {
      await onChanged();
    } catch {
      // the screen's own loader reports a failed reload; the change itself is saved
    } finally {
      // the reloaded rows are the truth now
      setPatches((prev) => {
        const next = { ...prev };
        delete next[invoice.id];
        return next;
      });
    }
  }, [onChanged, onDeleted]);

  /** The row as it is after this screen's last deletion action (null once deleted). */
  const apply = useCallback((invoice) => {
    const patch = invoice ? patches[invoice.id] : undefined;
    if (!patch) return invoice;
    if (patch.deleted) return null;
    return { ...invoice, deletion_request: patch.deletion_request || null };
  }, [patches]);

  const reasonValue = () => String(reasonRef.current?.value || '').trim();

  const request = useCallback(async (invoice, kind) => {
    const label = invoiceLabel(invoice);
    let saved = null;
    const ok = await confirm({
      title: `Ask to delete ${label}?`,
      subtitle: subtitleOf(invoice),
      description: 'An Admin must approve before it is deleted. Until then it stays in the payment schedule and the totals, marked “Deletion requested”.',
      children: <ReasonField inputRef={reasonRef} id="invoice-deletion-reason" label="Why should it go?" placeholder="e.g. Raised twice — duplicate of 276" testId="invoice-deletion-reason" />,
      tone: 'danger',
      icon: Trash2,
      confirmIcon: null,
      confirmLabel: 'Request deletion',
      note: 'Admins are notified. You can cancel the request from the same menu.',
      onConfirm: async () => {
        saved = (await api.post(`${invoiceApiPath(invoice, kind)}/deletion-request`, {
          reason: reasonValue(),
          expected_updated_at: invoice.updated_at ?? '',
        })).data;
      },
    });
    if (!ok) return false;
    notify.success('Deletion requested', { description: `${capital(label)} stays until an Admin approves it.` });
    await settle(invoice, { deletion_request: saved?.deletion_request || saved?.invoice?.deletion_request || { id: 'pending', requested_by_name: user?.name } });
    return true;
  }, [confirm, settle, user]);

  const remove = useCallback(async (invoice, kind) => {
    const label = invoiceLabel(invoice);
    const ok = await confirm({
      title: `Delete ${label}?`,
      subtitle: subtitleOf(invoice),
      description: 'It is removed from the payment schedule and the totals.',
      tone: 'danger',
      confirmLabel: isVariationInvoice(invoice, kind) ? 'Delete VO invoice' : 'Delete invoice',
      note: quickBooksNote(invoice, 'You can undo this from the toast.'),
      onConfirm: async () => { await api.delete(invoiceApiPath(invoice, kind)); },
    });
    if (!ok) return false;
    notify.success(`${capital(label)} deleted`);
    await settle(invoice, { deleted: true });
    return true;
  }, [confirm, settle]);

  const approve = useCallback(async (invoice, kind) => {
    const request = deletionRequestOf(invoice);
    if (!request) return false;
    const label = invoiceLabel(invoice);
    const when = request.requested_at ? ` on ${formatDate(request.requested_at)}` : '';
    const ok = await confirm({
      title: `Approve deleting ${label}?`,
      subtitle: subtitleOf(invoice),
      description: (
        <span>
          <b className="font-semibold text-ink">{text(request.requested_by_name) || 'Someone'}</b> asked{when} to delete it
          {text(request.reason) ? <>: “{text(request.reason)}”.</> : '.'} It is removed from the payment schedule and the totals.
        </span>
      ),
      tone: 'danger',
      confirmLabel: 'Approve and delete',
      note: quickBooksNote(invoice, `${text(request.requested_by_name) || 'The requester'} is notified. You can undo this from the toast.`),
      onConfirm: async () => {
        await api.post(`${invoiceApiPath(invoice, kind)}/deletion-request/approve`, {
          request_id: request.id,
          expected_updated_at: invoice.updated_at ?? '',
        });
      },
    });
    if (!ok) return false;
    notify.success(`${capital(label)} deleted`, { description: `Deletion approved · ${text(request.requested_by_name) || 'the requester'} is notified` });
    await settle(invoice, { deleted: true });
    return true;
  }, [confirm, settle]);

  const reject = useCallback(async (invoice, kind) => {
    const request = deletionRequestOf(invoice);
    if (!request) return false;
    const label = invoiceLabel(invoice);
    const ok = await confirm({
      title: `Keep ${label}?`,
      subtitle: `Deletion requested by ${text(request.requested_by_name) || 'someone'}${text(request.reason) ? ` — “${text(request.reason)}”` : ''}`,
      description: `The deletion request is rejected and ${label} stays as it is. ${text(request.requested_by_name) || 'The requester'} is told why.`,
      children: <ReasonField inputRef={reasonRef} id="invoice-deletion-reject-reason" label="Reason" required placeholder="e.g. The client still owes this amount" testId="invoice-deletion-reject-reason" />,
      icon: CircleX,
      confirmLabel: 'Reject request',
      onConfirm: async () => {
        const reason = reasonValue();
        if (!reason) throw new Error('Say why the deletion is rejected');
        await api.post(`${invoiceApiPath(invoice, kind)}/deletion-request/reject`, { reason, request_id: request.id });
      },
    });
    if (!ok) return false;
    notify.success('Deletion request rejected', { description: `${capital(label)} stays · ${text(request.requested_by_name) || 'the requester'} is notified` });
    await settle(invoice, { deletion_request: null });
    return true;
  }, [confirm, settle]);

  const cancel = useCallback(async (invoice, kind) => {
    const request = deletionRequestOf(invoice);
    if (!request) return false;
    try {
      await api.delete(`${invoiceApiPath(invoice, kind)}/deletion-request`);
    } catch (err) {
      notify.error('Could not cancel the deletion request', { description: getErrorMessage(err) });
      return false;
    }
    notify.success('Deletion request cancelled', { description: `${capital(invoiceLabel(invoice))} stays as it is.` });
    await settle(invoice, { deletion_request: null });
    return true;
  }, [settle]);

  /** Menu items for `invoice`; kind 'vo' for a register row of a VO invoice (else read from variation_id). */
  const menuItems = useCallback((invoice, { canEdit, kind, testIdPrefix } = {}) => deletionMenuItems(invoice, {
    user,
    canEdit,
    testIdPrefix,
    actions: {
      request: (row) => request(row, kind),
      remove: (row) => remove(row, kind),
      approve: (row) => approve(row, kind),
      reject: (row) => reject(row, kind),
      cancel: (row) => cancel(row, kind),
    },
  }), [user, request, remove, approve, reject, cancel]);

  return { user, isApprover: isApprover(user), apply, menuItems, request, remove, approve, reject, cancel };
}
